import React, { useState, useEffect } from 'react';
import { Palette, RotateCcw, Check, Layers, Square, Sun, Moon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useTheme, DEFAULT_THEME, SKIN_TONES, THEME_MODES } from '@/context/ThemeContext';
import { useFeatures } from '@/context/FeaturesContext';

// Helper to create light/dark variations of a theme while preserving brand colors
const createLightVariation = (brandColor, accentColor) => ({
  name: "Light Mode",
  mode: THEME_MODES.FLAT,
  primary: brandColor || "#3B82F6",
  secondary: accentColor || brandColor || "#3B82F6",
  background: "#FFFFFF",
  background_secondary: "#F8FAFC",
  text: "#1E293B",
  text_secondary: "#64748B",
  border: brandColor || "#3B82F6",
  logo_primary: brandColor || "#3B82F6",
  logo_secondary: accentColor || brandColor || "#3B82F6",
});

const createDarkVariation = (brandColor, accentColor) => ({
  name: "Dark Mode",
  mode: THEME_MODES.FLAT,
  primary: brandColor || "#3B82F6",
  secondary: accentColor || brandColor || "#3B82F6",
  background: "#0F172A",
  background_secondary: "#1E293B",
  text: "#F8FAFC",
  text_secondary: "#94A3B8",
  border: brandColor || "#3B82F6",
  logo_primary: brandColor || "#3B82F6",
  logo_secondary: accentColor || brandColor || "#3B82F6",
});

// Preset themes for quick selection
const PRESET_THEMES = [
  {
    name: "UnCloned Original",
    mode: THEME_MODES.GRADIENT,
    primary: "#df1283",
    secondary: "#FF7901",
    background: "#2D063E",
    background_secondary: "#4A0A66",
    text: "#FFFFFF",
    text_secondary: "#E0E0E0",
    border: "#df1283",
    logo_primary: "#df1283",
    logo_secondary: "#FF7901",
  },
  {
    name: "Ocean Blue",
    mode: THEME_MODES.GRADIENT,
    primary: "#0EA5E9",
    secondary: "#06B6D4",
    background: "#0C1222",
    background_secondary: "#1E293B",
    text: "#FFFFFF",
    text_secondary: "#94A3B8",
    border: "#0EA5E9",
    logo_primary: "#0EA5E9",
    logo_secondary: "#06B6D4",
  },
  {
    name: "Forest Green",
    mode: THEME_MODES.GRADIENT,
    primary: "#22C55E",
    secondary: "#84CC16",
    background: "#0A1F0A",
    background_secondary: "#14532D",
    text: "#FFFFFF",
    text_secondary: "#86EFAC",
    border: "#22C55E",
    logo_primary: "#22C55E",
    logo_secondary: "#84CC16",
  },
  {
    name: "Sunset Gold",
    mode: THEME_MODES.GRADIENT,
    primary: "#F59E0B",
    secondary: "#EF4444",
    background: "#1C1410",
    background_secondary: "#44403C",
    text: "#FFFFFF",
    text_secondary: "#FCD34D",
    border: "#F59E0B",
    logo_primary: "#F59E0B",
    logo_secondary: "#EF4444",
  },
  {
    name: "Lavender Dreams",
    mode: THEME_MODES.GRADIENT,
    primary: "#A855F7",
    secondary: "#EC4899",
    background: "#1E1033",
    background_secondary: "#3B0764",
    text: "#FFFFFF",
    text_secondary: "#D8B4FE",
    border: "#A855F7",
    logo_primary: "#A855F7",
    logo_secondary: "#EC4899",
  },
  {
    name: "Midnight",
    mode: THEME_MODES.GRADIENT,
    primary: "#6366F1",
    secondary: "#8B5CF6",
    background: "#0F0F1A",
    background_secondary: "#1E1E2E",
    text: "#FFFFFF",
    text_secondary: "#A5B4FC",
    border: "#6366F1",
    logo_primary: "#6366F1",
    logo_secondary: "#8B5CF6",
  },
  // Flat presets
  {
    name: "Clean White",
    mode: THEME_MODES.FLAT,
    primary: "#df1283",
    secondary: "#df1283",
    background: "#FFFFFF",
    background_secondary: "#F5F5F5",
    text: "#1F2937",
    text_secondary: "#6B7280",
    border: "#E5E7EB",
    logo_primary: "#df1283",
    logo_secondary: "#df1283",
  },
  {
    name: "Dark Minimal",
    mode: THEME_MODES.FLAT,
    primary: "#10B981",
    secondary: "#10B981",
    background: "#111111",
    background_secondary: "#1F1F1F",
    text: "#FFFFFF",
    text_secondary: "#9CA3AF",
    border: "#374151",
    logo_primary: "#10B981",
    logo_secondary: "#10B981",
  },
];

const ColorInput = ({ label, value, onChange, description }) => {
  const [inputValue, setInputValue] = useState(value);
  
  const handleInputChange = (e) => {
    let val = e.target.value;
    // Auto-add # if missing
    if (val && !val.startsWith('#')) {
      val = '#' + val;
    }
    setInputValue(val);
    
    // Only update if valid hex
    if (/^#[0-9A-Fa-f]{6}$/.test(val)) {
      onChange(val);
    }
  };
  
  const handleColorPickerChange = (e) => {
    const val = e.target.value;
    setInputValue(val);
    onChange(val);
  };

  // Sync input value when prop changes
  React.useEffect(() => {
    setInputValue(value);
  }, [value]);

  return (
    <div className="space-y-1">
      <label className="block text-sm" style={{ color: 'var(--theme-text-secondary)' }}>{label}</label>
      <div className="flex items-center gap-2">
        <div 
          className="w-10 h-10 rounded-lg overflow-hidden border-2 border-[var(--theme-primary)]/30 flex-shrink-0 relative"
          style={{ backgroundColor: value }}
        >
          <input
            type="color"
            value={value}
            onChange={handleColorPickerChange}
            className="absolute inset-0 w-full h-full cursor-pointer opacity-0"
          />
        </div>
        <input
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          placeholder="#000000"
          maxLength={7}
          className="flex-1 px-3 py-2 rounded-lg border border-[var(--theme-primary)]/30 text-sm font-mono focus:outline-none focus:border-[var(--theme-primary)]"
          style={{ 
            backgroundColor: 'rgba(0,0,0,0.3)',
            color: '#FFFFFF'
          }}
        />
      </div>
      {description && (
        <p className="text-xs" style={{ color: 'var(--theme-text-secondary)', opacity: 0.7 }}>{description}</p>
      )}
    </div>
  );
};

const ThemeCustomizer = ({ onSave, saving, clientDefaultTheme = null }) => {
  const { theme, updateTheme, resetTheme, skinTone, updateSkinTone } = useTheme();
  const { isFeatureEnabled, isB2BUser } = useFeatures();
  const [localTheme, setLocalTheme] = useState(theme);
  const [localSkinTone, setLocalSkinTone] = useState(skinTone);
  const [showPresets, setShowPresets] = useState(false);
  
  // B2B users are forced to flat mode - no gradients allowed
  useEffect(() => {
    if (isB2BUser && localTheme.mode !== THEME_MODES.FLAT) {
      const flatTheme = { ...localTheme, mode: THEME_MODES.FLAT };
      setLocalTheme(flatTheme);
      updateTheme(flatTheme);
    }
  }, [isB2BUser]);
  
  // For B2B users, create light/dark presets that preserve their brand colors
  const b2bBrandColor = clientDefaultTheme?.primary || clientDefaultTheme?.brand_color;
  const b2bAccentColor = clientDefaultTheme?.accent || clientDefaultTheme?.accent_color || b2bBrandColor;
  const b2bLightTheme = isB2BUser ? createLightVariation(b2bBrandColor, b2bAccentColor) : null;
  const b2bDarkTheme = isB2BUser ? createDarkVariation(b2bBrandColor, b2bAccentColor) : null;
  
  // Check if dark_mode feature is enabled for this user's tier
  const darkModeEnabled = isFeatureEnabled('dark_mode');
  
  // Determine if current theme is "light" based on background color brightness
  const isLightMode = localTheme.background && localTheme.background.toLowerCase() !== '#ffffff' 
    ? false  
    : (localTheme.text && localTheme.text !== '#FFFFFF');

  const handleColorChange = (key, value) => {
    const newTheme = { ...localTheme, [key]: value };
    setLocalTheme(newTheme);
    updateTheme(newTheme); // Live preview
  };
  
  const handleModeChange = (mode) => {
    const newTheme = { ...localTheme, mode };
    setLocalTheme(newTheme);
    updateTheme(newTheme);
  };

  const applyPreset = (preset) => {
    setLocalTheme(preset);
    updateTheme(preset);
    setShowPresets(false);
  };

  const handleReset = () => {
    // For B2B users, reset to their client's default theme, not UnCloned defaults
    if (isB2BUser && clientDefaultTheme) {
      // Ensure we have all required fields with fallbacks
      // Note: DB stores primary_color, secondary_color, background_primary, etc.
      const b2bDefault = {
        primary: clientDefaultTheme.primary || clientDefaultTheme.primary_color || clientDefaultTheme.brand_color || '#E91E63',
        secondary: clientDefaultTheme.secondary || clientDefaultTheme.secondary_color || clientDefaultTheme.accent_color || '#9C27B0',
        accent: clientDefaultTheme.accent || clientDefaultTheme.accent_color || clientDefaultTheme.secondary_color || '#9C27B0',
        brand_color: clientDefaultTheme.brand_color || clientDefaultTheme.primary_color || clientDefaultTheme.primary || '#E91E63',
        accent_color: clientDefaultTheme.accent_color || clientDefaultTheme.secondary_color || clientDefaultTheme.secondary || '#9C27B0',
        background: clientDefaultTheme.background || clientDefaultTheme.background_primary || '#0f0a1a',
        background_secondary: clientDefaultTheme.background_secondary || '#1a1025',
        text: clientDefaultTheme.text || clientDefaultTheme.text_primary || '#F8FAFC',
        text_secondary: clientDefaultTheme.text_secondary || '#94A3B8',
        border: clientDefaultTheme.border || clientDefaultTheme.border_color || '#2d2640',
        logo_primary: clientDefaultTheme.logo_primary || clientDefaultTheme.primary_color || clientDefaultTheme.brand_color || '#E91E63',
        logo_secondary: clientDefaultTheme.logo_secondary || clientDefaultTheme.secondary_color || clientDefaultTheme.accent_color || '#9C27B0',
        mode: THEME_MODES.FLAT, // Always flat for B2B
      };
      setLocalTheme(b2bDefault);
      updateTheme(b2bDefault);
    } else {
      setLocalTheme(DEFAULT_THEME);
      resetTheme();
    }
  };
  
  const handleSkinToneChange = (toneId) => {
    setLocalSkinTone(toneId);
    updateSkinTone(toneId);
  };
  
  const isGradientMode = localTheme.mode !== THEME_MODES.FLAT;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Palette className="w-5 h-5 text-[var(--theme-primary)]" />
          <h3 className="text-lg font-semibold" style={{ color: 'var(--theme-text)' }}>Theme Colors</h3>
        </div>
        <Button
          onClick={handleReset}
          variant="ghost"
          size="sm"
          className="text-[var(--theme-text-secondary)] hover:text-[var(--theme-text)] hover:bg-[var(--theme-background-secondary)]"
        >
          <RotateCcw className="w-4 h-4 mr-1" />
          Reset
        </Button>
      </div>
      
      {/* Theme Mode Toggle - HIDDEN for B2B users (they only get flat mode) */}
      {!isB2BUser && (
        <div className="p-4 rounded-xl border border-[var(--theme-primary)]/30 bg-[var(--theme-background-secondary)]/30">
          <p className="text-sm font-medium mb-3" style={{ color: 'var(--theme-text)' }}>Theme Style</p>
          <div className="flex gap-2">
            <button
              onClick={() => handleModeChange(THEME_MODES.GRADIENT)}
              className={`flex-1 flex items-center justify-center gap-2 p-3 rounded-lg transition-all ${
                isGradientMode
                  ? 'bg-[var(--theme-primary)]/30 ring-2 ring-[var(--theme-primary)]'
                  : 'bg-[var(--theme-background)] hover:bg-[var(--theme-background-secondary)]'
              }`}
            >
              <Layers className="w-5 h-5" style={{ color: 'var(--theme-text)' }} />
              <span className="text-sm font-medium" style={{ color: 'var(--theme-text)' }}>Gradient</span>
            </button>
            <button
              onClick={() => handleModeChange(THEME_MODES.FLAT)}
              className={`flex-1 flex items-center justify-center gap-2 p-3 rounded-lg transition-all ${
                !isGradientMode
                  ? 'bg-[var(--theme-primary)]/30 ring-2 ring-[var(--theme-primary)]'
                  : 'bg-[var(--theme-background)] hover:bg-[var(--theme-background-secondary)]'
              }`}
            >
              <Square className="w-5 h-5" style={{ color: 'var(--theme-text)' }} />
              <span className="text-sm font-medium" style={{ color: 'var(--theme-text)' }}>Flat</span>
            </button>
          </div>
          <p className="text-xs mt-2" style={{ color: 'var(--theme-text-secondary)' }}>
            {isGradientMode 
              ? 'Uses gradient backgrounds and buttons (more vibrant)'
              : 'Uses solid colors only (cleaner, easier to customize)'
            }
          </p>
        </div>
      )}
      
      {/* Dark/Light Mode Toggle - Only shown if feature is enabled */}
      {darkModeEnabled && (
        <div className="p-4 rounded-xl border border-[var(--theme-primary)]/30 bg-[var(--theme-background-secondary)]/30">
          <p className="text-sm font-medium mb-3" style={{ color: 'var(--theme-text)' }}>Appearance</p>
          <div className="flex gap-2">
            <button
              onClick={() => {
                // Switch to dark theme - keep accent colors
                const darkColors = {
                  background: '#111111',
                  background_secondary: '#1F1F1F',
                  text: '#FFFFFF',
                  text_secondary: '#9CA3AF',
                  border: localTheme.primary,
                };
                const newTheme = { ...localTheme, ...darkColors };
                setLocalTheme(newTheme);
                updateTheme(newTheme);
              }}
              className={`flex-1 flex items-center justify-center gap-2 p-3 rounded-lg transition-all ${
                localTheme.background !== '#FFFFFF' && localTheme.background !== '#F5F5F5'
                  ? 'bg-[var(--theme-primary)]/30 ring-2 ring-[var(--theme-primary)]'
                  : 'bg-[var(--theme-background)] hover:bg-[var(--theme-background-secondary)]'
              }`}
            >
              <Moon className="w-5 h-5" style={{ color: 'var(--theme-text)' }} />
              <span className="text-sm font-medium" style={{ color: 'var(--theme-text)' }}>Dark</span>
            </button>
            <button
              onClick={() => {
                // Switch to light theme - keep accent colors
                const lightColors = {
                  background: '#FFFFFF',
                  background_secondary: '#F5F5F5',
                  text: '#1F2937',
                  text_secondary: '#6B7280',
                  border: '#E5E7EB',
                };
                const newTheme = { ...localTheme, ...lightColors };
                setLocalTheme(newTheme);
                updateTheme(newTheme);
              }}
              className={`flex-1 flex items-center justify-center gap-2 p-3 rounded-lg transition-all ${
                localTheme.background === '#FFFFFF' || localTheme.background === '#F5F5F5'
                  ? 'bg-[var(--theme-primary)]/30 ring-2 ring-[var(--theme-primary)]'
                  : 'bg-[var(--theme-background)] hover:bg-[var(--theme-background-secondary)]'
              }`}
            >
              <Sun className="w-5 h-5" style={{ color: 'var(--theme-text)' }} />
              <span className="text-sm font-medium" style={{ color: 'var(--theme-text)' }}>Light</span>
            </button>
          </div>
          <p className="text-xs mt-2" style={{ color: 'var(--theme-text-secondary)' }}>
            Choose your preferred background brightness
          </p>
        </div>
      )}
      
      {/* Emoji Skin Tone Selector */}
      <div className="p-4 rounded-xl border border-[var(--theme-primary)]/30 bg-[var(--theme-background-secondary)]/30">
        <p className="text-sm font-medium mb-3" style={{ color: 'var(--theme-text)' }}>Emoji Skin Tone</p>
        <div className="flex flex-wrap gap-2">
          {SKIN_TONES.map((tone) => (
            <button
              key={tone.id}
              onClick={() => handleSkinToneChange(tone.id)}
              className={`text-2xl p-2 rounded-lg transition-all ${
                localSkinTone === tone.id 
                  ? 'bg-[var(--theme-primary)]/30 ring-2 ring-[var(--theme-primary)] scale-110' 
                  : 'hover:bg-[var(--theme-background-secondary)] hover:scale-105'
              }`}
              title={tone.name}
            >
              {tone.sample}
            </button>
          ))}
        </div>
        <p className="text-xs mt-2" style={{ color: 'var(--theme-text-secondary)' }}>
          This applies to all hand emojis throughout the app
        </p>
      </div>

      {/* Preset Themes */}
      <div>
        <button
          onClick={() => setShowPresets(!showPresets)}
          className="text-sm text-[var(--theme-primary)] hover:text-[var(--theme-secondary)] transition-colors"
        >
          {showPresets ? 'Hide presets' : 'Choose a preset theme'}
        </button>
        
        {showPresets && (
          <div className="mt-3 space-y-4">
            {/* Gradient Presets - HIDDEN for B2B users */}
            {!isB2BUser && (
              <div>
                <p className="text-xs mb-2" style={{ color: 'var(--theme-text-secondary)' }}>Gradient Themes</p>
                <div className="grid grid-cols-2 gap-2">
                  {PRESET_THEMES.filter(p => p.mode !== THEME_MODES.FLAT).map((preset) => (
                    <button
                      key={preset.name}
                      onClick={() => applyPreset(preset)}
                      className="p-3 rounded-lg border border-[var(--theme-primary)]/20 hover:border-[var(--theme-primary)]/50 transition-all text-left group"
                      style={{ background: preset.background }}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <div 
                          className="w-4 h-4 rounded-full" 
                          style={{ background: `linear-gradient(135deg, ${preset.primary}, ${preset.secondary})` }}
                        />
                        {localTheme.primary === preset.primary && 
                         localTheme.mode === preset.mode && (
                          <Check className="w-4 h-4 text-green-400 ml-auto" />
                        )}
                      </div>
                      <span className="text-xs" style={{ color: preset.text }}>
                        {preset.name}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}
            
            {/* Flat Presets */}
            <div>
              <p className="text-xs mb-2" style={{ color: 'var(--theme-text-secondary)' }}>
                {isB2BUser ? 'Theme Modes' : 'Flat Themes'}
              </p>
              <div className="grid grid-cols-2 gap-2">
                {/* For B2B users, show only Light/Dark with their brand colors preserved */}
                {isB2BUser ? (
                  <>
                    {/* B2B Light Mode */}
                    <button
                      onClick={() => applyPreset(b2bLightTheme)}
                      className="p-3 rounded-lg border border-[var(--theme-primary)]/20 hover:border-[var(--theme-primary)]/50 transition-all text-left group"
                      style={{ background: b2bLightTheme.background }}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <Sun className="w-4 h-4" style={{ color: b2bLightTheme.primary }} />
                        {localTheme.background === b2bLightTheme.background && (
                          <Check className="w-4 h-4 text-green-400 ml-auto" />
                        )}
                      </div>
                      <span className="text-xs" style={{ color: b2bLightTheme.text }}>
                        Light Mode
                      </span>
                    </button>
                    {/* B2B Dark Mode */}
                    <button
                      onClick={() => applyPreset(b2bDarkTheme)}
                      className="p-3 rounded-lg border border-[var(--theme-primary)]/20 hover:border-[var(--theme-primary)]/50 transition-all text-left group"
                      style={{ background: b2bDarkTheme.background }}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <Moon className="w-4 h-4" style={{ color: b2bDarkTheme.primary }} />
                        {localTheme.background === b2bDarkTheme.background && (
                          <Check className="w-4 h-4 text-green-400 ml-auto" />
                        )}
                      </div>
                      <span className="text-xs" style={{ color: b2bDarkTheme.text }}>
                        Dark Mode
                      </span>
                    </button>
                  </>
                ) : (
                  /* For regular users, show all flat presets */
                  PRESET_THEMES.filter(p => p.mode === THEME_MODES.FLAT).map((preset) => (
                    <button
                      key={preset.name}
                      onClick={() => applyPreset(preset)}
                      className="p-3 rounded-lg border border-[var(--theme-primary)]/20 hover:border-[var(--theme-primary)]/50 transition-all text-left group"
                      style={{ background: preset.background }}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <div 
                          className="w-4 h-4 rounded-full" 
                          style={{ background: preset.primary }}
                        />
                        {localTheme.primary === preset.primary && 
                         localTheme.mode === preset.mode && (
                          <Check className="w-4 h-4 text-green-400 ml-auto" />
                        )}
                      </div>
                      <span className="text-xs" style={{ color: preset.text }}>
                        {preset.name}
                      </span>
                    </button>
                  ))
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Custom Color Inputs - Reordered for better UX */}
      <div className="space-y-4 pt-2">
        <ColorInput
          label="Text Color"
          value={localTheme.text || DEFAULT_THEME.text}
          onChange={(val) => handleColorChange('text', val)}
          description="Main text color"
        />
        
        <ColorInput
          label="Secondary Text"
          value={localTheme.text_secondary || DEFAULT_THEME.text_secondary}
          onChange={(val) => handleColorChange('text_secondary', val)}
          description="Muted text, labels, hints"
        />
        
        <ColorInput
          label="Background"
          value={localTheme.background}
          onChange={(val) => handleColorChange('background', val)}
          description="Main background color"
        />
        
        <ColorInput
          label="Background Secondary"
          value={localTheme.background_secondary}
          onChange={(val) => handleColorChange('background_secondary', val)}
          description="Cards, inputs, panels"
        />
        
        <ColorInput
          label="Primary Color"
          value={localTheme.primary}
          onChange={(val) => handleColorChange('primary', val)}
          description={isGradientMode ? "Buttons, highlights, gradient start" : "Buttons, highlights, accents"}
        />
        
        {/* Secondary Color - Only show for gradient mode */}
        {isGradientMode && (
          <ColorInput
            label="Secondary Color"
            value={localTheme.secondary}
            onChange={(val) => handleColorChange('secondary', val)}
            description="Gradient end color, hover states"
          />
        )}
        
        {/* Border Color */}
        <ColorInput
          label="Border Color"
          value={localTheme.border || localTheme.primary}
          onChange={(val) => handleColorChange('border', val)}
          description="Card borders, dividers, outlines"
        />
        
        {/* Logo Colors Section - Conditional based on mode */}
        <div className="pt-4 border-t border-[var(--theme-border)]/20">
          <p className="text-sm font-medium mb-3" style={{ color: 'var(--theme-text)' }}>
            {isGradientMode ? 'Logo Gradient Colors' : 'Logo Color'}
          </p>
          
          <div className="space-y-4">
            <ColorInput
              label={isGradientMode ? "Logo Primary" : "Logo Color"}
              value={localTheme.logo_primary || localTheme.primary}
              onChange={(val) => handleColorChange('logo_primary', val)}
              description={isGradientMode ? "Logo gradient start color" : "Logo text color"}
            />
            
            {/* Logo Secondary - Only show for gradient mode */}
            {isGradientMode && (
              <ColorInput
                label="Logo Secondary"
                value={localTheme.logo_secondary || localTheme.secondary}
                onChange={(val) => handleColorChange('logo_secondary', val)}
                description="Logo gradient end color"
              />
            )}
          </div>
        </div>
      </div>

      {/* Preview */}
      <div 
        className="p-4 rounded-xl" 
        style={{ 
          background: localTheme.background,
          border: `2px solid ${localTheme.border || localTheme.primary}40`
        }}
      >
        <p className="text-xs mb-2" style={{ color: localTheme.text_secondary || '#AAAAAA' }}>Preview</p>
        <p className="text-sm mb-3" style={{ color: localTheme.text || '#FFFFFF' }}>This is how your text will look</p>
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <button 
            className="px-4 py-2 rounded-lg text-white text-sm font-medium"
            style={{ 
              background: isGradientMode 
                ? `linear-gradient(to right, ${localTheme.primary}, ${localTheme.secondary})`
                : localTheme.primary
            }}
          >
            Primary Button
          </button>
          <div 
            className="px-3 py-2 rounded-lg text-sm"
            style={{ 
              background: localTheme.background_secondary,
              color: localTheme.text_secondary || '#AAAAAA',
              border: `1px solid ${localTheme.border || localTheme.primary}30`
            }}
          >
            Card Element
          </div>
        </div>
        {/* Logo Preview */}
        <div 
          className="mt-3 pt-3"
          style={{ borderTop: `1px solid ${localTheme.border || localTheme.primary}30` }}
        >
          <p className="text-xs mb-2" style={{ color: localTheme.text_secondary || '#AAAAAA' }}>Logo Preview</p>
          <div 
            className="text-xl font-black"
            style={{ 
              background: isGradientMode
                ? `linear-gradient(90deg, ${localTheme.logo_primary || localTheme.primary}, ${localTheme.logo_secondary || localTheme.secondary})`
                : localTheme.logo_primary || localTheme.primary,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}
          >
            UnCloned® Journal
          </div>
        </div>
      </div>
    </div>
  );
};

export default ThemeCustomizer;
